import propTypes from 'prop-types'
import {connect} from 'react-redux'
import {get} from 'lodash'
import Presentation from '../../components/pivotTable'
import {accountHasFeature, userHasPermission} from 'helpers/user'
import {features, permissions} from 'helpers/constants'
import {convertObjectToArray, getVisibleColumns, isFiltering, isSearching} from 'helpers/pivotTable'
import * as actions from '../../actions/pivotTable'
import {
  exportData,
  reorderEntity,
  requestData,
  setColumnSizes,
  setGridStretch,
  setGroups,
  setVisibility,
  setVisibleColumns,
  updatePageSize,
} from "actions/pivotTable"

export const extractState = (state, key) => {
  return get(state, key, null)
}

const mapStateToProps = (state, ownProps) => {
  const {
    stateKey,
    icon,
    pluralNoun,
    singularNoun,
    columns,
    groupingAllowed,
    fixedGroups,
    highlight,
    listEntityClass,
    fixedDataTop,
    fixedDataBottom,
    useServerSideSearching,
    onSearchData,
  } = ownProps

  let {
    groups = null,
    gridStretch = null,
  } = ownProps

  // Get my portion of the state
  let myState = extractState(state, stateKey)
  if (myState == null) {
    console.error('Pivot Table [container]: No pivotTable element found in state under key [' + stateKey + ']')
    myState = {}
  }

  // Extract running properties from state with defaults
  const {
    columnSizes = {},
    search = '',
    sortColumn,
    sortDirection = 'asc',
    pageSize = 30,
    page = 0,
    exporting,
    exportingRows,
    exportedRows,
  } = myState
  groups = fixedGroups || myState.groups || groups || []

  let data = myState.data || {}
  if (ownProps.onData)
    data = {...data, data: ownProps.onData(data.data)}

  if (gridStretch === null)
    gridStretch = myState.gridStretch

  const {fetching = false, count = 0} = data
  data = data.data || []

  // Specified or default visible columns
  const visibleColumns = getVisibleColumns(myState, columns)

  if (ownProps.useServerSideSearching === false) {
    data = onSearchData(data, search)
  }

  return {
    stateKey,
    icon,
    pluralNoun,
    singularNoun,
    allColumns: columns,
    groups,
    groupingAllowed,
    search,
    sortColumn,
    sortDirection,
    highlight,
    pageSize,
    page,
    gridStretch,
    listEntityClass,
    visibleColumns,
    columnSizes,
    loading: fetching,
    data,
    totalRecords: count,
    fixedDataTop,
    fixedDataBottom,
    useServerSideSearching,
    onSearchData,
    exporting,
    exportingRows,
    exportedRows,
    exportingAllowed: (ownProps.exportingAllowed !== false) &&
      accountHasFeature(state.auth.user, features.EXPORT) &&
      userHasPermission(state.auth.user, permissions.EXPORT_DATA),
    isSearching: isSearching(myState),
    isFiltering: isFiltering(convertObjectToArray(myState)),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const {stateKey, refreshingAllowed = true, onSelectEntity, onReorderEntity} = ownProps
  return {
    onSelectEntity,
    onUpdateVisibleColumns: (visibleColumns) => dispatch(setVisibleColumns(stateKey, visibleColumns)),
    onUpdateColumnSizes: (columnSizes) => dispatch(setColumnSizes(stateKey, columnSizes)),
    onUpdateGroups: (groups) => dispatch(setGroups(stateKey, groups)),
    onSort: (sortColumn, sortDirection) => dispatch(actions.sort(stateKey, sortColumn, sortDirection)),
    onSearch: (search) => dispatch(actions.search(stateKey, search)),
    onPage: (page) => dispatch(actions.page(stateKey, page)),
    onExport: (columns, fileName) => dispatch(exportData(stateKey, columns, fileName)),
    onRefresh: refreshingAllowed ? () => dispatch(requestData(stateKey)) : null,
    onGridStretchChanged: (gridStretch) => dispatch(setGridStretch(stateKey, gridStretch)),
    onReorderEntity: (entity, fromIndex, toIndex) => {
      if (onReorderEntity) {
        onReorderEntity(entity, fromIndex, toIndex) // managed by owner
      } else {
        dispatch(reorderEntity(stateKey, entity, fromIndex, toIndex))
      }
    },
    onSetVisibility: (visible) => dispatch(setVisibility(stateKey, visible)),
    onUpdatePageSize: (pageSize, page) => dispatch(updatePageSize(stateKey, pageSize, page)),
  }
}

const PivotTable = connect(mapStateToProps, mapDispatchToProps)(Presentation)

export const pivotTablePropTypes = {
  stateKey: propTypes.string.isRequired,
  icon: propTypes.string.isRequired,
  pluralNoun: propTypes.string.isRequired,
  singularNoun: propTypes.string.isRequired,
  columns: propTypes.object.isRequired,             // column definitions (see note below)
  groups: propTypes.arrayOf(propTypes.string),      // default groups
  fixedGroups: propTypes.arrayOf(propTypes.string), // fixed groups (overrides configured grouping)
  groupingAllowed: propTypes.bool,
  refreshingAllowed: propTypes.bool,
  gridStretch: propTypes.bool,
  showReloads: propTypes.bool,
  highlight: propTypes.func,
  listEntityClass: propTypes.oneOfType([
    propTypes.func,     // a React class to instantiate
    propTypes.element,  // a React element to clone
  ]),
  onSelectEntity: propTypes.func,
  onData: propTypes.func,
  onSetVisibility: propTypes.func,
  isAddButtonDisabled: propTypes.bool,
}

PivotTable.propTypes = pivotTablePropTypes

PivotTable.defaultProps = {
  groupingAllowed: true,
}

// columns defines each column as an object keyed by its key
// e.g. columns: { myColumn: { ... } }
export const pivotTableColumnPropType = {
  key: propTypes.string.isRequired,     // the unique key for this column (must be the same as its key in columns)
  label: propTypes.string.isRequired,   // the label to display in the column header
  data: propTypes.func.isRequired,      // (entity) return the data to display in each row
  size: propTypes.number.isRequired,    // the default size for this column
  visible: propTypes.bool.isRequired,   // whether this column should be visible by default (included in the grid)
  order: propTypes.number,              // the order in which this column appears in the grid, if visible
  align: propTypes.oneOf([              // the horizontal alignment of this column (defaults to left)
    'left',
    'centre',
    'right',
    'justify',
  ]),
  alias: propTypes.string,              // an alias to display when selecting this column
                                        //   (only required if label is blank)
  style: propTypes.object,              // override the style of this column in the grid
  export: propTypes.func,               // (entity) return how data should be formatted when exporting to a CSV file
                                        //   (uses data if not specified)
  group: propTypes.func,                // (entity) return a value used to group entities together
                                        //   when this column is grouped
  groupData: propTypes.func,            // (entity) return how data should be rendered in the group header,
                                        //   when grouped (uses data if not specified)
  className: propTypes.string,          // a class name to apply to cells within this column
}

export default PivotTable
